import React, { useEffect, useState } from "react";
import axios from "axios";
import { Play, ArrowRight, X, Trash2, Check, Mic } from "lucide-react";

export const API = process.env.REACT_APP_BACKEND_URL;
export const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
export const C = { bg: "var(--coach-bg, #0b1a16)", card: "rgba(241,236,221,0.05)", line: "rgba(214,190,130,0.28)", gold: "#d6be82", cream: "#f1ecdd", dim: "rgba(241,236,221,0.7)", mist: "rgba(241,236,221,0.45)" };
export const SERIF = '"Cormorant Garamond", Georgia, serif';
export const SANS = '"Manrope", sans-serif';

export const Chooser = ({ identities, onChoose, title = "Choose your Golf Coach" }) => (
  <div className="space-y-6" data-testid="coach-chooser">
    <h1 className="text-4xl sm:text-5xl font-light" style={{ fontFamily: SERIF, color: C.cream }}>{title}</h1>
    <div className="grid grid-cols-2 gap-4">
      {Object.entries(identities).map(([k, v]) => (
        <button key={k} type="button" onClick={() => onChoose(k)} data-testid={`coach-choose-${k}`}
          className="rounded-3xl overflow-hidden text-left transition-transform duration-300 hover:-translate-y-1" style={{ border: `1px solid ${C.line}` }}>
          <img src={v.image} alt={v.name} className="w-full aspect-[3/4] object-cover object-top" data-testid={`coach-choose-img-${k}`} />
          <div className="p-4 text-2xl" style={{ fontFamily: SERIF, color: C.cream }}>{v.name}</div>
        </button>
      ))}
    </div>
  </div>
);

const glass = { background: "rgba(241,236,221,0.045)", border: "1px solid rgba(214,190,130,0.22)", backdropFilter: "blur(14px)" };
const tiny = { color: C.gold, fontFamily: SANS };
const Ring = ({ pct, children }) => (
  <span className="relative w-11 h-11 shrink-0 inline-flex items-center justify-center rounded-full" style={{ background: "rgba(214,190,130,0.12)", color: C.gold }}>
    <svg className="absolute inset-0 -rotate-90" viewBox="0 0 40 40"><circle cx="20" cy="20" r="18.5" fill="none" stroke="rgba(214,190,130,0.18)" strokeWidth="1.2" />
      <circle cx="20" cy="20" r="18.5" fill="none" stroke={C.gold} strokeWidth="1.4" strokeLinecap="round" strokeDasharray={116.2} strokeDashoffset={116.2 * (1 - pct)} style={{ transition: "stroke-dashoffset 300ms linear" }} /></svg>
    {children}
  </span>
);
const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Level 2 — compact card in the chat; the Coach guides the Reset full screen (GuidedReset).
export const InlineReset = ({ tool, coachName, onStart }) => (
  <div className="mt-3 max-w-sm rounded-2xl px-3 py-2.5 flex items-center gap-3" style={glass} data-testid={`coach-reset-${tool.id}`}>
    {tool.done ? <Ring pct={1}><Check size={15} /></Ring> : (
      <button type="button" onClick={onStart} aria-label="Begin" data-testid="coach-reset-play" className="rounded-full transition-transform duration-200 active:scale-95">
        <Ring pct={0}><Play size={14} className="ml-0.5" /></Ring>
      </button>
    )}
    <div className="min-w-0 flex-1">
      <div className="text-[0.55rem] uppercase tracking-[0.26em]" style={tiny}>{tool.done ? "Done" : `${tool.ritual ? "Ritual" : "Reset"} · with ${coachName || "me"}`}</div>
      <div className="truncate text-base leading-tight" style={{ fontFamily: SERIF, color: C.cream }}>{tool.name}</div>
    </div>
    {tool.done && <span className="sr-only" data-testid="coach-reset-done">Done</span>}
  </div>
);

// Level 3 — one tap into the deeper experience, with the why already said by the coach.
export const DeeperCard = ({ tool, onOpen }) => (
  <button type="button" onClick={onOpen} data-testid={`coach-deeper-${tool.id}`}
    className="mt-3 max-w-sm w-full rounded-2xl px-3 py-2.5 flex items-center gap-3 text-left transition-transform duration-200 hover:-translate-y-0.5" style={glass}>
    <Ring pct={0}><ArrowRight size={14} /></Ring>
    <span className="min-w-0 flex-1">
      <span className="block text-[0.55rem] uppercase tracking-[0.26em]" style={tiny}>Take me deeper · {tool.label}</span>
      <span className="block truncate text-base leading-tight" style={{ fontFamily: SERIF, color: C.cream }}>{tool.name}</span>
    </span>
  </button>
);

// Shown after a call ends: what you worked on + one thing to practise.
export const RecapCard = ({ recap, onClose, onContinue, name }) => (
  <div className="relative mt-6 rounded-2xl px-4 py-4" style={glass} data-testid="coach-recap">
    <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3" style={{ color: C.mist }} data-testid="coach-recap-close"><X size={14} /></button>
    <div className="text-[0.55rem] uppercase tracking-[0.26em]" style={tiny}>What we worked on</div>
    <p className="mt-1.5 pr-5 text-lg leading-snug" style={{ fontFamily: SERIF, color: C.cream }} data-testid="coach-recap-worked">{recap.worked_on}</p>
    <div className="mt-3 text-[0.55rem] uppercase tracking-[0.26em]" style={tiny}>One thing to practise</div>
    <p className="mt-1 text-base italic" style={{ fontFamily: SERIF, color: C.dim }} data-testid="coach-recap-practise">{recap.practise}</p>
    <button type="button" onClick={onContinue} data-testid="coach-recap-continue"
      className="mt-4 w-full rounded-full py-3 text-xs uppercase tracking-[0.24em] font-bold inline-flex items-center justify-center gap-2 transition-transform duration-200 active:scale-95"
      style={{ background: C.gold, color: "#0b1430", fontFamily: SANS }}>
      <Mic size={14} /> Keep talking with {name}
    </button>
  </div>
);

export const MemorySheet = ({ coachId, name, onClose }) => {
  const [m, setM] = useState(null);
  const load = () => axios.get(`${API}/api/coach/${coachId}/memory`, auth()).then((r) => setM(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const del = (i) => axios.delete(`${API}/api/coach/${coachId}/memory${i === undefined ? "" : `?fact=${i}`}`, auth()).then(() => (i === undefined ? window.location.reload() : load()));
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3" style={{ background: "rgba(4,10,8,0.8)", backdropFilter: "blur(14px)" }} data-testid="coach-memory-sheet">
      <div className="w-full max-w-md max-h-[80vh] overflow-y-auto rounded-3xl p-6 relative" style={{ background: C.bg, border: `1px solid ${C.line}` }}>
        <button type="button" onClick={onClose} className="absolute right-4 top-4" style={{ color: C.cream }} data-testid="coach-memory-close"><X size={18} /></button>
        <h2 className="text-3xl font-light" style={{ fontFamily: SERIF, color: C.cream }}>What {name} remembers</h2>
        {!m ? null : (
          <>
            <ul className="mt-5 space-y-2">
              {m.life_facts.length === 0 && <li className="italic" style={{ color: C.mist, fontFamily: SERIF }}>Nothing yet.</li>}
              {m.life_facts.map((f, i) => (
                <li key={i} className="flex items-start justify-between gap-3 text-sm" style={{ color: C.dim, fontFamily: SANS }} data-testid={`coach-memory-fact-${i}`}>
                  <span>{f}</span>
                  <button type="button" onClick={() => del(i)} aria-label="Forget this" style={{ color: C.mist }} data-testid={`coach-memory-forget-${i}`}><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
            {!!m.favourites?.length && (
              <div className="mt-6" data-testid="coach-memory-favourites">
                <div className="text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: C.gold, fontFamily: SANS }}>Your top Resets</div>
                <ul className="mt-2 space-y-1.5">
                  {m.favourites.map((f, i) => (
                    <li key={f.id} className="flex items-baseline justify-between gap-3" data-testid={`coach-memory-favourite-${i}`}>
                      <span className="text-lg" style={{ fontFamily: SERIF, color: C.cream }}>{f.name}</span>
                      <span className="text-xs" style={{ color: C.mist, fontFamily: SANS }}>helped {f.helped} of {f.n}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!!m.experiences.length && (
              <div className="mt-6" data-testid="coach-memory-experiences">
                <div className="text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: C.gold, fontFamily: SANS }}>Done together</div>
                <p className="mt-2 text-sm" style={{ color: C.dim, fontFamily: SANS }}>{[...new Set(m.experiences.map((e) => e.name))].join(" · ")}</p>
              </div>
            )}
            {!!m.session_notes.length && (
              <div className="mt-6" data-testid="coach-memory-notes">
                <div className="text-[0.6rem] uppercase tracking-[0.28em]" style={{ color: C.gold, fontFamily: SANS }}>Recent conversations</div>
                <ul className="mt-2 space-y-1.5">{m.session_notes.slice(-5).reverse().map((n, i) => <li key={i} className="text-sm" style={{ color: C.dim, fontFamily: SANS }}>{n.note}</li>)}</ul>
              </div>
            )}
            <button type="button" onClick={() => del()} className="mt-6 text-xs underline" style={{ color: C.mist }} data-testid="coach-memory-forget-all">Forget everything and start fresh</button>
          </>
        )}
      </div>
    </div>
  );
};

// Talking face: 4 aligned photos; mouth follows the voice loudness (Web Audio), blinks on its own.
let audioCtx = null;
export const unlockAudio = () => {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
  } catch { /* no Web Audio */ }
};
export const getAudioCtx = () => { unlockAudio(); return audioCtx; };
// iPhone only lets a sound element play on its own once a tap has started it — call this inside taps.
export const primeAudio = (el) => {
  unlockAudio();
  if (!el || !el.paused || el.dataset.primed) return;
  el.dataset.primed = "1";
  el.src = "/silence.mp3";
  el.play().catch(() => { delete el.dataset.primed; });
};

// One shared voice element, unlocked by the first tap anywhere, so guides can speak without a button.
export const coachAudio = Object.assign(new Audio(), { preload: "auto" });
window.addEventListener("pointerdown", () => primeAudio(coachAudio), { capture: true });

export { TalkingPortrait } from "./TalkingPortrait";
